import classes from './styles.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, postWithFile } from '../../core/client';
import { IsNullOrEmpty } from '../../core/utility';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InfoIcon from '@mui/icons-material/Info';
import { CustomButton } from '../../components/buttons';
import UploadInput from '../../components/inputs/upload-input';
import Typography from '@mui/material/Typography';
import WarningModal from '../../components/warning-modal';
import SuccessModal from '../../components/success-modal';
import DetailedInfo from '../shared/DetailedInfo';

interface InputErrorModel {
    name: string;
    errorText: string;
}

interface RelativeDocumentInfo {
    message: string;
    memberShipRequestId: number;
}

interface StateModel {
    file: File | null;
    fileName: string;
    membershipRequestId: number;
    hasRelativeFile: boolean;
    canUpload: boolean;
}

interface SaveRelativeDocumentResponse {
    success: boolean;
    message: string;
}

const useQuery = () => {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
};

const RelativeJoinDocument = () => {
    const query = useQuery();
    const relativeGuid = query.get('rg');

    const [state, setState] = useState<StateModel>({
        file: null,
        fileName: '',
        membershipRequestId: 0,
        hasRelativeFile: false,
        canUpload: false
    });

    const [loading, setLoading] = useState(false);
    const [loadingSendForm, setLoadingSendForm] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [initialErrorText, setInitialErrorText] = useState('');
    const [inputErrors, setInputErrors] = useState<InputErrorModel[]>([]);
    const [warningOpen, setWarningOpen] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [fileAlreadyExists, setFileAlreadyExists] = useState(false);
    const uploadInputRef = useRef<any>(null);

    const handleWarningOpen = (msg: string) => {
        setMessage(msg);
        setWarningOpen(true);
    };

    const handleSuccessOpen = (msg: string) => {
        setMessage(msg);
        setSuccessOpen(true);
    };

    const handleWarningClose = () => setWarningOpen(false);
    const handleSuccessClose = () => setSuccessOpen(false);

    useEffect(() => {
        const initialLoad = async () => {
            setLoading(true);

            if (!relativeGuid || IsNullOrEmpty(relativeGuid)) {
                handleWarningOpen("Sayfa hatalı çağrıldı. Lütfen geçerli bir bağlantı kullanın.");
                setState(prev => ({
                    ...prev,
                    canUpload: false
                }));
                setLoading(false);
                return;
            }

            const response = await loadRelativeInfo(relativeGuid);
            if (response) {
                const { message, memberShipRequestId } = response;
                if (IsNullOrEmpty(message) && memberShipRequestId > 0) {
                    setState(prev => ({
                        ...prev,
                        membershipRequestId: memberShipRequestId,
                        canUpload: true,
                        hasRelativeFile: false
                    }));
                } else {
                    handleWarningOpen(message || 'Kayıt bulunamadı');
                    setState(prev => ({
                        ...prev,
                        membershipRequestId: 0,
                        canUpload: false,
                        hasRelativeFile: false
                    }));
                    setFileAlreadyExists(true);
                }
            }

            setLoading(false);
        };

        initialLoad();
    }, [relativeGuid]);


    const loadRelativeInfo = async (guid: string) => {
        const { data, fail, errorMessage } = await get<RelativeDocumentInfo>(`api/membership/get-relative-document/${guid}`);
        if (fail) {
            setInitialErrorText(errorMessage);
        }
        return data;
    };

    const handleFileInput = (files: FileList | []) => {
        setState(prev => ({
            ...prev,
            file: files.length > 0 ? files[0] : null,
            fileName: files.length > 0 ? files[0].name : ''
        }));
    };

    const validateFileInput = (files: File[]) => {
        const fileTypes = ['application/pdf'];
        const maxSize = 2097152;

        for (const file of files) {
            if (!fileTypes.includes(file.type)) {
                return 'Lütfen yalnızca PDF formatında dosya yükleyin.';
            }
            if (file.size > maxSize) {
                return `Dosya boyutu en fazla 2 MB olmalı. (${file.name})`;
            }
        }

        return null;
    };

    const setError = (name: keyof StateModel, errorText = 'Zorunlu alan') => {
        setInputErrors(prev => [...prev, { name, errorText }]);
    };

    const validate = (model: StateModel): boolean => {
        setInputErrors([]);
        let isValid = true;

        if (!model.hasRelativeFile && model.file === null) {
            setError('file', 'Geçerli bir belge yükleyiniz.');
            isValid = false;
        } else if (model.file) {
            const fileError = validateFileInput([model.file]);
            if (fileError) {
                setError('file', fileError);
                isValid = false;
            }
        }

        return isValid;
    };

    const handleSave = async (model: StateModel) => {
        if (!validate(model)) return;

        setLoadingSendForm(true);

        const { data, fail, errorMessage } = await postWithFile<SaveRelativeDocumentResponse>(
            'api/membership/relative-document/add-file',
            {
                file: model.file,
                membershipRequestId: model.membershipRequestId,
                relativeGuid: relativeGuid
            }
        );

        setLoadingSendForm(false);

        if (fail || !data?.success) {
            setErrorText(data?.message || errorMessage || 'Bilinmeyen hata.');
            handleWarningOpen(data?.message || errorMessage || 'Bilinmeyen hata.');

            setState(prev => ({
                ...prev,
                file: null,
                fileName: '',
                canUpload: false
            }));
            uploadInputRef.current?.handleClear();

            return;
        }

        setMessage('Doküman başarıyla yüklendi.');
        handleSuccessOpen('Doküman başarıyla yüklendi.');

        setState(prev => ({
            ...prev,
            file: null,
            fileName: '',
            canUpload: false
        }));
        uploadInputRef.current?.handleClear();

        setFileAlreadyExists(true);
    };

    return (
        <div className={classes.RelativeJoinDocument}>
            <div className={classes.overlay}>
                <div className={classes.container}>
                    <div className={classes.form}>
                        <h1>AİLE YAKINI DOKÜMAN YÜKLEME</h1>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {state.hasRelativeFile ? (
                                    <Box display="flex" marginTop="8px">
                                        <InfoIcon fontSize="small" color="info" />
                                        <Typography variant="body1" color="textSecondary" marginLeft="8px">
                                            Aile yakınına ait doküman mevcuttur.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <p>
                                            E-devlet üzerinden alınacak Nüfus Kayıt Örneği Belgesi menüsünden Nüfus Kayıt Örneği Tipi'nin{' '}
                                            <strong>Nüfus Aile</strong> seçilmesi gerekmektedir.
                                        </p>
                                        <UploadInput
                                            ref={uploadInputRef}
                                            text="DOSYA SEÇ"
                                            disabled={!state.canUpload}
                                            options={{
                                                accept: 'application/pdf',
                                                onChange: handleFileInput,
                                                multiple: false,
                                            }}
                                            errorText={inputErrors.find(x => x.name === 'file')?.errorText}
                                        />
                                        <Box display="flex" marginTop="8px">
                                            <InfoIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="textSecondary" ml={0.5}>
                                                Yüklediğiniz belge PDF formatında olmalıdır.
                                            </Typography>
                                        </Box>
                                        <Box display="flex" marginTop="8px" sx={{ display: fileAlreadyExists ? 'flex' : 'none' }}>
                                            <Typography variant="body2" color="textSecondary" ml={0.5}>
                                                Yüklediğin dosyanın hatalı veya eksik olduğunu düşünüyorsan destek@kocailem.com.tr mail adresine mail iletebilirsin.
                                            </Typography>
                                        </Box>
                                        <DetailedInfo />
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        <Box display="flex" justifyContent="center" marginBottom={2}>
                            <CustomButton
                                text="DOKÜMANI KAYDET"
                                className="btn-primary"
                                disabled={
                                    loadingSendForm ||
                                    loading ||
                                    initialErrorText.length > 0 ||
                                    state.file === null ||
                                    !state.canUpload
                                }
                                loading={loadingSendForm}
                                onClick={() => handleSave(state)}
                            />
                        </Box>
                    </div>
                </div>
            </div>
            <WarningModal open={warningOpen} message={message} handleClose={handleWarningClose} />
            <SuccessModal open={successOpen} message={message} handleClose={handleSuccessClose} />
        </div>
    );
};

export default RelativeJoinDocument;
